import * as React from "react";
import { useState } from "react";
import { Field, withTypes } from "react-final-form";
import axios from "axios";

import {
    Button,
    CardActions,
    CircularProgress,
    TextField,
    Checkbox,
    FormControlLabel,
    Card,
    CardContent,
    Typography,
    Divider,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Paper,
    Tabs,
    Tab,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import RefreshIcon from "@material-ui/icons/Refresh";

import { Notification, useTranslate, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigPost } from "../getConfig";
import UpdatePromocode from "./UpdatePromocode";
import BulkCreatePromocode from "./BulkCreatePromocode";

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(3),
        maxWidth: 800,
        margin: "0 auto",
        width: "100%",
        boxSizing: "border-box",
    },
    containerFull: {
        padding: theme.spacing(3),
        maxWidth: "100%",
        margin: 0,
        width: "100%",
        minHeight: "calc(100vh - 64px)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
    },
    tabsPaper: {
        marginBottom: theme.spacing(3),
        borderRadius: theme.spacing(1),
        flexShrink: 0,
    },
    updateTabContent: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        minWidth: 0,
        maxWidth: "100%",
        width: "100%",
        overflow: "hidden",
    },
    card: {
        marginBottom: theme.spacing(3),
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(2),
    },
    cardIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    formSection: {
        marginBottom: theme.spacing(2),
    },
    fieldRow: {
        display: "flex",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    field: {
        flex: 1,
    },
    checkboxGroup: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    actions: {
        padding: theme.spacing(2, 0),
    },
    passwordDisplay: {
        backgroundColor: "#f5f5f5",
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1),
        marginTop: theme.spacing(2),
        fontFamily: "monospace",
        fontSize: "1.1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    warningText: {
        color: theme.palette.warning.main,
        marginTop: theme.spacing(1),
        fontWeight: "bold",
    },
    divider: {
        margin: theme.spacing(3, 0),
    },
    resetForm: {
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "flex-end",
    },
}));

const renderInput = ({
    meta: { touched, error } = { touched: false, error: undefined },
    input: { ...inputProps },
    ...props
}: any) => (
    <TextField
        error={!!(touched && error)}
        helperText={touched && error}
        {...inputProps}
        {...props}
        fullWidth
        variant="outlined"
        size="small"
    />
);

const renderCheckbox = ({ input, label }: any) => (
    <FormControlLabel
        control={
            <Checkbox
                name={input.name}
                checked={!!input.checked}
                onChange={input.onChange}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                color="primary"
            />
        }
        label={label}
    />
);

interface FormValues {
    code?: string;
    amount?: string;
    from_date?: string;
    to_date?: string;
    sws_introducer?: boolean;
    remortgage_introducer?: boolean;
    direct_debit_introducer?: boolean;
    affiliate_name?: string;
    generate_password?: boolean;
}

const { Form } = withTypes<FormValues>();

const CreatePromoCode = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState<{ code: string; password: string } | null>(null);
    const [resetCode, setResetCode] = useState("");
    const translate = useTranslate();
    const notify = useNotify();
    const classes = useStyles();

    const handleSubmit = async (data: FormValues) => {
        setLoading(true);

        const payload = {
            code: data.code,
            amount: data.amount,
            from_date: data.from_date,
            to_date: data.to_date,
            sws_introducer: String(data.sws_introducer || false),
            remortgage_introducer: String(data.remortgage_introducer || false),
            direct_debit_introducer: String(data.direct_debit_introducer || false),
            affiliate_name: data.affiliate_name || null,
            generate_password: data.generate_password || false,
        };

        try {
            const headers = getConfigPost();
            const response = await axios.post(ENDPOINTS.CREATE_PROMOCODE, payload, { headers });
            const responseData = response.data as { code?: string; password?: string; message?: string };

            if (responseData?.password) {
                setGeneratedPassword({
                    code: responseData.code || (payload.code as string),
                    password: responseData.password,
                });
                setPasswordDialogOpen(true);
            } else {
                notify("Promo code created successfully", "info");
            }
        } catch (error: any) {
            notify(error?.response?.data?.message || `Failed to create promo code`, "error", "", undefined, 50000);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        if (!resetCode.trim()) {
            notify("Please enter a promo code", "warning");
            return;
        }

        setResetLoading(true);

        try {
            const headers = getConfigPost();
            const response = await axios.post(
                ENDPOINTS.AFFILIATE_PASSWORD_RESET,
                { code: resetCode.trim() },
                { headers }
            );

            const data = response.data as { code?: string; password?: string };
            if (data?.password) {
                setGeneratedPassword({
                    code: data.code || resetCode.trim(),
                    password: data.password,
                });
                setPasswordDialogOpen(true);
                setResetCode("");
            }
        } catch (error: any) {
            notify(error?.response?.data?.message || `Failed to reset password`, "error", "", undefined, 50000);
        } finally {
            setResetLoading(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        notify("Password copied to clipboard", "info");
    };

    const validate = (values: FormValues) => {
        const errors: FormValues = {};
        if (!values.code) errors.code = translate("ra.validation.required");
        if (!values.amount) errors.amount = translate("ra.validation.required");
        if (!values.from_date) errors.from_date = translate("ra.validation.required");
        return errors;
    };

    return (
        <div
            className={
                activeTab === 1 || activeTab === 2 ? classes.containerFull : classes.container
            }
        >
            <Paper className={classes.tabsPaper} elevation={1}>
                <Tabs
                    value={activeTab}
                    onChange={(_, value) => setActiveTab(value)}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label="Create Promo Code" />
                    <Tab label="Update Promo Code" />
                    <Tab label="Bulk Create" />
                </Tabs>
            </Paper>

            {activeTab === 0 && (
                <>
                    <Card className={classes.card}>
                        <CardContent>
                            <div className={classes.cardHeader}>
                                <AddCircleIcon className={classes.cardIcon} />
                                <Typography variant="h6">Create Promo Code</Typography>
                            </div>

                            <Form
                                onSubmit={handleSubmit}
                                validate={validate}
                                render={({ handleSubmit }) => (
                                    <form onSubmit={handleSubmit} noValidate>
                                        <div className={classes.formSection}>
                                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                                Basic Information
                                            </Typography>
                                            <div className={classes.fieldRow}>
                                                <div className={classes.field}>
                                                    <Field
                                                        name="code"
                                                        component={renderInput}
                                                        label="Promo Code"
                                                        disabled={loading}
                                                        placeholder="e.g., PAYFAST"
                                                    />
                                                </div>
                                                <div className={classes.field}>
                                                    <Field
                                                        name="amount"
                                                        component={renderInput}
                                                        label="Amount (GBP)"
                                                        disabled={loading}
                                                        type="number"
                                                        placeholder="e.g., 5.00"
                                                    />
                                                </div>
                                            </div>
                                            <div className={classes.fieldRow}>
                                                <div className={classes.field}>
                                                    <Field
                                                        name="from_date"
                                                        component={renderInput}
                                                        label="From Date"
                                                        type="date"
                                                        InputLabelProps={{ shrink: true }}
                                                        disabled={loading}
                                                    />
                                                </div>
                                                <div className={classes.field}>
                                                    <Field
                                                        name="to_date"
                                                        component={renderInput}
                                                        label="To Date (Optional)"
                                                        type="date"
                                                        InputLabelProps={{ shrink: true }}
                                                        disabled={loading}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <Divider className={classes.divider} />

                                        <div className={classes.formSection}>
                                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                                Introducer Type
                                            </Typography>
                                            <div className={classes.checkboxGroup}>
                                                <Field
                                                    name="sws_introducer"
                                                    component={renderCheckbox}
                                                    label="SWS Introducer"
                                                    type="checkbox"
                                                />
                                                <Field
                                                    name="remortgage_introducer"
                                                    component={renderCheckbox}
                                                    label="Remortgage Introducer"
                                                    type="checkbox"
                                                />
                                                <Field
                                                    name="direct_debit_introducer"
                                                    component={renderCheckbox}
                                                    label="Direct Debit Introducer"
                                                    type="checkbox"
                                                />
                                            </div>
                                        </div>

                                        <Divider className={classes.divider} />

                                        <div className={classes.formSection}>
                                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                                Affiliate Dashboard Access
                                            </Typography>
                                            <Box mb={2}>
                                                <Field
                                                    name="affiliate_name"
                                                    component={renderInput}
                                                    label="Affiliate Name (Optional)"
                                                    disabled={loading}
                                                    placeholder="e.g., PayFast Partner"
                                                />
                                            </Box>
                                            <Field
                                                name="generate_password"
                                                component={renderCheckbox}
                                                label="Generate password for affiliate dashboard login"
                                                type="checkbox"
                                            />
                                            <Typography variant="caption" color="textSecondary" display="block">
                                                If checked, a password will be generated and shown once after creation.
                                                The affiliate can use the promo code + password to login at affiliates.spriveapps.com
                                            </Typography>
                                        </div>

                                        <CardActions className={classes.actions}>
                                            <Button
                                                variant="contained"
                                                type="submit"
                                                color="primary"
                                                fullWidth
                                                disabled={loading}
                                                startIcon={loading ? <CircularProgress size={20} /> : <AddCircleIcon />}
                                            >
                                                Create Promo Code
                                            </Button>
                                        </CardActions>
                                    </form>
                                )}
                            />
                        </CardContent>
                    </Card>

                    <Card className={classes.card}>
                        <CardContent>
                            <div className={classes.cardHeader}>
                                <VpnKeyIcon className={classes.cardIcon} />
                                <Typography variant="h6">Reset Affiliate Password</Typography>
                            </div>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Generate a new password for an existing promo code. The old password will be invalidated.
                            </Typography>
                            <div className={classes.resetForm}>
                                <TextField
                                    label="Promo Code"
                                    variant="outlined"
                                    size="small"
                                    value={resetCode}
                                    onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                                    placeholder="e.g., PAYFAST"
                                    style={{ flex: 1 }}
                                />
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handlePasswordReset}
                                    disabled={resetLoading || !resetCode.trim()}
                                    startIcon={resetLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                                >
                                    Reset Password
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}

            {activeTab === 1 && (
                <div className={classes.updateTabContent}>
                    <UpdatePromocode />
                </div>
            )}

            {activeTab === 2 && (
                <div className={classes.updateTabContent}>
                    <BulkCreatePromocode />
                </div>
            )}

            <Dialog
                open={passwordDialogOpen}
                onClose={() => setPasswordDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Affiliate Password Generated
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Password for promo code <strong>{generatedPassword?.code}</strong>:
                    </Typography>
                    <Paper className={classes.passwordDisplay}>
                        <span>{generatedPassword?.password}</span>
                        <IconButton
                            size="small"
                            onClick={() => copyToClipboard(generatedPassword?.password || "")}
                            title="Copy to clipboard"
                        >
                            <FileCopyIcon />
                        </IconButton>
                    </Paper>
                    <Typography className={classes.warningText}>
                        Save this password now! It cannot be retrieved later.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                        Share these credentials with the affiliate:
                    </Typography>
                    <ul>
                        <li>Dashboard URL: <strong>affiliates.spriveapps.com</strong></li>
                        <li>Promo Code: <strong>{generatedPassword?.code}</strong></li>
                        <li>Password: <strong>{generatedPassword?.password}</strong></li>
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => copyToClipboard(generatedPassword?.password || "")}
                        color="primary"
                        startIcon={<FileCopyIcon />}
                    >
                        Copy Password
                    </Button>
                    <Button
                        onClick={() => setPasswordDialogOpen(false)}
                        color="primary"
                        variant="contained"
                    >
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            <Notification />
        </div>
    );
};

export default CreatePromoCode;
